import { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip } from "recharts";
import { EXO_DRIVERS } from "@/data/driversExo";
import { DRIVER_BY_KEY, DEFAULT_RANKING } from "@/components/exo/driversExoShared";

const PATIENT_COLOR = "#B58C67";
const PRATICIEN_COLOR = "#4A3B36";
const PROCHE_COLORS = ["#8A9A6D", "#5A7D9A", "#A05A8A", "#B0553B", "#C08552"];
const TICK_INK = { fill: "#6E5C53", fontSize: 12 };
const TICK_MUTED = { fill: "#a1907f", fontSize: 9 };

const safeRank = (r) => (r && r.length === DEFAULT_RANKING.length ? r : DEFAULT_RANKING);
const intensity = (ranking, key) => DEFAULT_RANKING.length - safeRank(ranking).indexOf(key);
const position = (ranking, key) => safeRank(ranking).indexOf(key) + 1;
const procheLabel = (pr, i) => (pr.name ? pr.name : `Proche ${i + 1}`);

export const buildRadarData = (patientRanking, proches, praticienRanking) =>
  EXO_DRIVERS.map((d) => {
    const row = { subject: d.name, key: d.key, patient: intensity(patientRanking, d.key) };
    proches.forEach((pr, i) => { row[`proche${i}`] = intensity(pr.ranking, d.key); });
    if (praticienRanking && praticienRanking.length) row.praticien = intensity(praticienRanking, d.key);
    return row;
  });

export const computeGaps = (patientRanking, proches, praticienRanking) =>
  EXO_DRIVERS.map((d) => {
    const pPos = position(patientRanking, d.key);
    const procheGaps = proches.map((pr) => Math.abs(pPos - position(pr.ranking, d.key)));
    const procheAvg = procheGaps.length ? Math.round((procheGaps.reduce((a, b) => a + b, 0) / procheGaps.length) * 10) / 10 : null;
    const pratGap = praticienRanking && praticienRanking.length ? Math.abs(pPos - position(praticienRanking, d.key)) : null;
    return { key: d.key, name: d.name, color: d.color, pPos, procheAvg, pratGap };
  });

const gapTone = (g) => (g === null ? "" : g === 0 ? "Accord" : g >= 2 ? "Écart notable" : "Écart");
const gapColor = (g) => (g === null ? "#d8ccbe" : g === 0 ? "#8A9A6D" : g >= 2 ? "#B0553B" : "#C08552");

const GapBar = ({ label, gap, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center justify-between text-xs mb-1">
      <span className="text-[var(--ink-soft)]">{label}</span>
      <span style={{ color: gapColor(gap) }}>{gapTone(gap)}{gap > 0 ? ` — ${gap} place${gap > 1 ? "s" : ""}` : ""}</span>
    </div>
    <div className="h-3 rounded-full bg-[var(--beige)] overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(gap / 4) * 100}%`, backgroundColor: gapColor(gap) }} />
    </div>
  </div>
);

const divergenceSentence = (gaps, proches, praticienRanking) => {
  const withProche = gaps.filter((g) => g.procheAvg !== null);
  if (!withProche.length) return null;
  const top = [...withProche].sort((a, b) => b.procheAvg - a.procheAvg)[0];
  const multi = proches.length > 1;
  const partsPrat = praticienRanking && praticienRanking.length
    ? (() => { const tp = [...gaps].sort((a, b) => b.pratGap - a.pratGap)[0]; return tp.pratGap > 0 ? ` Avec le praticien, l'écart le plus marqué porte sur « ${tp.name} » (${tp.pratGap} place${tp.pratGap > 1 ? "s" : ""}).` : " Le classement du praticien rejoint le vôtre sur les cinq drivers."; })()
    : "";
  if (top.procheAvg === 0) return `Vos classements et ${multi ? "ceux de vos proches" : "celui de votre proche"} coïncident sur les cinq drivers.${partsPrat}`;
  if (top.procheAvg < 2) return `Vos regards sont globalement proches : la différence la plus visible porte sur « ${top.name} » (${top.procheAvg} place${top.procheAvg > 1 ? "s" : ""}).${partsPrat}`;
  return `La divergence la plus marquée concerne « ${top.name} » : vous le placez ${top.pPos}${top.pPos === 1 ? "er" : "e"}, ${multi ? "vos proches le situent en moyenne" : "votre proche le situe"} à ${top.procheAvg} place${top.procheAvg > 1 ? "s" : ""} d'écart. C'est un point de dialogue privilégié.${partsPrat}`;
};

export const DriversRadar = ({ patientRanking, proches, praticienRanking, pdf = false }) => {
  const data = buildRadarData(patientRanking, proches, praticienRanking);
  const gaps = computeGaps(patientRanking, proches, praticienRanking);
  const hasPrat = praticienRanking && praticienRanking.length > 0;
  const sentence = divergenceSentence(gaps, proches, praticienRanking);
  const compact = typeof window !== "undefined" && window.innerWidth < 640;

  const box = "rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-7 print-break";
  const chart = (
    <>
      <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">{pdf ? "Regards croisés" : "Vos regards croisés"}</h3>
      <p className="text-sm text-[var(--ink-soft)] mb-4">Intensité perçue de chaque driver (1ᵉʳ du classement = 5, dernier = 1). Trois temps : {pdf ? "patient, proches, praticien" : "vous, vos proches, puis le praticien"}.</p>
      <div className="bg-white/70 rounded-2xl border border-[var(--border)] p-2">
        <ResponsiveContainer width="100%" height={pdf ? 320 : compact ? 340 : 380}>
          <RadarChart data={data} outerRadius={compact ? "55%" : "70%"}>
            <PolarGrid stroke="#d8ccbe" />
            <PolarAngleAxis dataKey="subject" tick={compact ? { ...TICK_INK, fontSize: 10 } : TICK_INK} />
            <PolarRadiusAxis angle={90} domain={[0, 5]} tickCount={6} tick={TICK_MUTED} />
            <Radar isAnimationActive={!pdf} name={pdf ? "Patient" : "Vous"} dataKey="patient" stroke={PATIENT_COLOR} fill={PATIENT_COLOR} fillOpacity={0.4} />
            {proches.map((pr, i) => (
              <Radar isAnimationActive={!pdf} key={i} name={procheLabel(pr, i)} dataKey={`proche${i}`} stroke={PROCHE_COLORS[i % PROCHE_COLORS.length]} fill={PROCHE_COLORS[i % PROCHE_COLORS.length]} fillOpacity={0.15} />
            ))}
            {hasPrat && <Radar isAnimationActive={!pdf} name="Praticien" dataKey="praticien" stroke={PRATICIEN_COLOR} fill={PRATICIEN_COLOR} fillOpacity={0.08} strokeDasharray="6 4" strokeWidth={2} />}
            <Legend itemSorter={() => 0} />
            <Tooltip formatter={(v) => `${v} / 5`} itemSorter={() => 0} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </>
  );
  const ecarts = (
    <div className={pdf ? "" : "mt-6"} data-testid={pdf ? undefined : "exo-entourage-gaps"}>
      <h4 className="text-sm font-semibold text-[var(--ink)] mb-3">Écarts de classement par driver</h4>
      <div className="space-y-4">
        {gaps.map((g) => (
          <div key={g.key} className="rounded-xl bg-white/60 border border-[var(--border)] p-3" data-testid={pdf ? undefined : `exo-entourage-gaprow-${g.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: g.color }} />
              <span className="text-sm font-semibold text-[var(--ink)]">{g.name}</span>
              <span className="text-xs text-[var(--ink-soft)]">{pdf ? "patient" : "vous"} : {g.pPos}<sup>e</sup></span>
            </div>
            <div className="space-y-1.5">
              {g.procheAvg !== null && <GapBar label={proches.length > 1 ? "Proches (moy.)" : "Proche"} gap={g.procheAvg} testid={pdf ? undefined : `exo-entourage-gapbar-proche-${g.key}`} />}
              {g.pratGap !== null && <GapBar label="Praticien" gap={g.pratGap} testid={pdf ? undefined : `exo-entourage-gapbar-praticien-${g.key}`} />}
            </div>
          </div>
        ))}
      </div>
      {sentence && <p className="mt-4 text-sm text-[var(--ink)] leading-relaxed italic" data-testid={pdf ? undefined : "exo-entourage-divergence"}>{sentence.replace(/^Vos /, pdf ? "Les " : "Vos ")}</p>}
    </div>
  );

  if (pdf) return <div className="space-y-4"><div className={box}>{chart}</div><div className={box}>{ecarts}</div></div>;
  return <div className={box} data-testid="exo-entourage-radar">{chart}{ecarts}</div>;
};

export const PraticienRanking = ({ initial, onSave, saved, text = "En séance, le praticien propose son propre classement au vu des résultats. Il s'ajoute au radar et aux écarts ci-dessus." }) => {
  const [open, setOpen] = useState(Boolean(initial && initial.length));
  const [ranking, setRanking] = useState(() => safeRank(initial));
  const move = (key, dir) =>
    setRanking((prev) => {
      const arr = [...prev];
      const i = arr.indexOf(key);
      const j = i + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });

  return (
    <div className="rounded-3xl border border-dashed border-[var(--gold-light)] bg-white/50 p-6 sm:p-7 no-print" data-testid="exo-praticien-block">
      <p className="text-xs uppercase tracking-[0.15em] text-[var(--gold-light)] mb-1">Troisième temps</p>
      <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">Regard du praticien</h3>
      <p className="text-sm text-[var(--ink-soft)] mb-4" data-testid="exo-praticien-text">{text}</p>
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="exo-praticien-open" className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">Saisir le classement du praticien</button>
      ) : (
        <>
          <ul className="space-y-2 mb-4">
            {ranking.map((key, i) => {
              const d = DRIVER_BY_KEY[key];
              return (
                <li key={key} data-testid={`exo-praticien-rank-${key}`} className="flex items-center gap-3 rounded-2xl border border-[var(--border)] bg-[var(--sand)] p-3">
                  <span className="flex items-center justify-center h-8 w-8 rounded-full text-white font-semibold shrink-0" style={{ backgroundColor: d.color }}>{i + 1}</span>
                  <span className="flex-1 font-medium text-[var(--ink)]">{d.name}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(key, -1)} disabled={i === 0} data-testid={`exo-praticien-up-${key}`} className={`h-8 w-8 rounded-lg flex items-center justify-center border transition-colors ${i === 0 ? "border-[var(--border)] text-[var(--beige)] cursor-not-allowed" : "border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white"}`}><ChevronUp size={18} /></button>
                    <button onClick={() => move(key, 1)} disabled={i === ranking.length - 1} data-testid={`exo-praticien-down-${key}`} className={`h-8 w-8 rounded-lg flex items-center justify-center border transition-colors ${i === ranking.length - 1 ? "border-[var(--border)] text-[var(--beige)] cursor-not-allowed" : "border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white"}`}><ChevronDown size={18} /></button>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => onSave(ranking)} data-testid="exo-praticien-save" className="px-8 py-3 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">Enregistrer le classement du praticien</button>
            {saved && <span className="text-xs text-[var(--ink-soft)]" data-testid="exo-praticien-saved">Classement du praticien enregistré.</span>}
          </div>
        </>
      )}
    </div>
  );
};
